import type { Language } from '@eformulieren/engine'
import type { Projection, Verwijzingen } from '@eformulieren/model'
import { groupedSeries } from '../field/controls/Subgrid.js'
import { seriesRolesFor } from './seriesRoles.js'

/** De eerdere antwoorden op één vraag uit één reeks, zoals de modus met historie ze toont. */
export interface QuestionHistoryEntry {
  /** Het bouwblok waar de reeks vandaan komt, voor de naam van de regio (R-58). */
  readonly bron: string
  readonly kolommen: readonly string[]
  readonly rijen: readonly Record<string, unknown>[]
}

/**
 * Per vraag de eerdere antwoorden, voor de modus met historie (R-137, R-62, D-73).
 *
 * **Uit de reeksen die er al zijn, en uit niets anders.** Elk `subgrid` in de projectie met een
 * opgeloste verwijzing en een rol `vraag` levert zijn groepen, met dezelfde groepering als de tabel
 * in de bronsectie (`groupedSeries`). Er komt geen tweede weg naar het dossier bij: wie de
 * bronsectie niet in zijn projectie heeft, krijgt ook bij de vraag niets te zien (R-63, R-68).
 *
 * **De sleutel is de waarde van de vraag-eigenschap, en een criterium is de vraag die haar draagt.**
 * In de reeks van een beoordelend blok is die waarde de criterium-identiteit, die buiten het
 * formulier stabiel is (R-150) en in een gelijke kopie voor een volgend uitvraagmoment blijft
 * (`copy` in de engine). Een andere koppeling tussen een vraag in de reeks en een veld op het
 * formulier is er niet, en hier wordt er geen verzonnen: een groep zonder criterium met die
 * identiteit blijft alleen in de tabel van de bronsectie staan.
 *
 * **Alleen de reeks van het eigen blok.** R-62 gaat over de eerdere instanties van hetzelfde
 * bouwblok, en alleen daarvan leest een subgrid alle standen (`shownStands` in de engine). Een
 * subgrid op een blok eerder in de keten toont de laatste stand van dat andere blok, en bij de vraag
 * zou die lezen als de eerdere antwoorden van deze invuller. Is het eigen blok niet bekend, dan
 * staat er niets bij de vraag: liever geen historie dan een stand van een ander blok.
 *
 * Alleen de velden van een sectie zelf: een subgrid in een rij van een herhaalgroep toont per rij,
 * en een reeks per rij bestaat niet (`resolveVerwijzingen`).
 */
export function historyPerQuestion(
  projection: Pick<Projection, 'secties'>,
  verwijzingen: Verwijzingen | undefined,
  ownBlock: string | undefined,
): ReadonlyMap<string, readonly QuestionHistoryEntry[]> {
  const out = new Map<string, QuestionHistoryEntry[]>()
  if (verwijzingen === undefined || ownBlock === undefined) return out
  for (const section of projection.secties) {
    for (const field of section.velden) {
      if (field.control !== 'subgrid' || field.bron === undefined || field.bron.contract !== ownBlock) continue
      const outcome = verwijzingen[field.id]
      if (outcome === undefined) continue
      const grouped = groupedSeries(outcome, seriesRolesFor(field.bron))
      if (grouped === undefined) continue
      for (const group of grouped.groepen) {
        if (group.titel === '') continue
        const list = out.get(group.titel) ?? []
        list.push({ bron: field.bron.contract, kolommen: grouped.kolommen, rijen: group.rijen })
        out.set(group.titel, list)
      }
    }
  }
  return out
}


/**
 * Het label van elk criterium op dit formulier, op zijn identiteit, in de taal van de lezer (R-150,
 * R-143): de titel van een groep in een reeks waarvan de vraag een criterium-identiteit is.
 *
 * Uit de projectie, dus de vraag zoals dit formulier haar stelt; een identiteit die hier niet
 * voorkomt houdt haar waarde als titel (`Subgrid`).
 */
export function questionLabels(projection: Pick<Projection, 'secties'>, language: Language): ReadonlyMap<string, string> {
  const out = new Map<string, string>()
  for (const section of projection.secties) {
    for (const field of section.velden) {
      if (field.control === 'criterium') out.set(field.criteriumId, field.label[language])
    }
  }
  return out
}

/**
 * De vraagwaarden waarvan de groep in de modus met historie bij haar criterium staat (R-137).
 *
 * Precies de sleutels van {@link historyPerQuestion} waarvoor dit formulier een criterium heeft:
 * alleen die tekent `SectionPanel` bij de vraag, dus alleen die kan de bronsectie weglaten.
 */
export function placedQuestions(
  history: ReadonlyMap<string, readonly QuestionHistoryEntry[]>,
  labels: ReadonlyMap<string, string>,
): ReadonlySet<string> {
  return new Set([...history.keys()].filter((question) => labels.has(question)))
}
